import { VitaBeneficiario } from '../services/vita-beneficiario.service';

/**
 * Helpers de la libreta de beneficiarios internacionales.
 *
 * Nacieron dentro del wizard de envío (`vita-calculator-demo`) y se extrajeron
 * acá para que el módulo de gestión (`recipient-internacional`) muestre las
 * mismas tarjetas sin duplicar la lógica.
 *
 * Principio no-code: no se hardcodean keys por país; solo se prueba un orden
 * universal sobre el JSON libre `datosBancarios`.
 */

/**
 * Países a los que HOY se puede enviar (tienen rail productivo). El registro
 * de beneficiarios se limita a estos: no tiene sentido dar de alta a alguien
 * al que después no se le puede transferir.
 *
 * Vive en el front porque el backend todavía no publica un flag por país —
 * `listCountries()` devuelve los países activos del proveedor, no los que
 * tienen rail. Mover acá el día que exista ese flag; si no, sumar un tercer
 * rail obliga a tocar esta constante.
 */
export const PAISES_CON_RAIL: readonly string[] = ['CO', 'PE'];

export function tieneRailDeEnvio(codigoPais: string | null | undefined): boolean {
  return PAISES_CON_RAIL.includes((codigoPais || '').toUpperCase());
}

/**
 * Banderas locales por ISO. OJO: el `flagIcon` que manda el backend es un
 * EMOJI (🇨🇴), no una URL — no sirve para `<img src>` (en Windows/Chrome se
 * renderiza como letras).
 */
const FLAG_ASSETS: Record<string, string> = {
  AR: 'assets/flags/argentina.png',
  CL: 'assets/flags/chile.png',
  CO: 'assets/flags/colombia.png',
  PE: 'assets/flags/peru.png',
  VE: 'assets/flags/venezuela.png',
};

/** Ruta del PNG de la bandera, o null si no hay asset para ese país. */
export function flagAssetFor(codigoPais: string | null | undefined): string | null {
  return FLAG_ASSETS[(codigoPais || '').toUpperCase()] || null;
}

/** Nombre completo (persona natural o razón social). */
export function displayNombre(b: VitaBeneficiario): string {
  const full = `${b.nombre || ''} ${b.apellidos || ''}`.trim();
  if (full) return full;
  const companyName = (b.datosBancarios || {})['company_name'];
  if (companyName) return String(companyName);
  return '—';
}

/** Nombre visible del banco: col denormalizada → label de variante → raw. */
export function displayBanco(b: VitaBeneficiario): string {
  return b.bancoNombre || b.varianteLabel || (b.variante || '').toUpperCase();
}

/**
 * Máscara de cuenta: col denormalizada → derivada del primer identificador
 * que aparezca en `datosBancarios` (los valores cortos son selects/tipos, no
 * identificadores).
 */
export function displayCuentaMask(b: VitaBeneficiario): string {
  if (b.cuentaMask) return b.cuentaMask;
  const d = b.datosBancarios || {};
  for (const k of Object.keys(d).sort()) {
    const v = d[k];
    if (v === null || v === undefined || v === '') continue;
    const s = String(v);
    if (s.length < 4) continue;
    return '●●●●' + s.slice(-4);
  }
  return '—';
}

/** Texto sobre el que corre el buscador de la libreta. */
export function textoBusqueda(b: VitaBeneficiario): string {
  return `${displayNombre(b)} ${b.numeroId || ''} ${displayBanco(b)} ${b.email || ''}`.toLowerCase();
}
